import type { MonthlyLeadStat } from '../types/lead'

type Props = {
  stats: MonthlyLeadStat[]
}

const LeadsMonthlyDashboard = ({ stats }: Props) => {
  if (stats.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200/80 bg-white p-6 shadow-sm mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Dashboard mensal</h3>
        <p className="text-sm text-gray-500">Sem dados por mês para exibir.</p>
      </div>
    )
  }

  const maxCount = Math.max(...stats.map((s) => s.count), 1)
  const maxQuantidade = Math.max(...stats.map((s) => s.quantidade), 1)
  const totalMeses = stats.length
  const leadsNoPeriodo = stats.reduce((sum, s) => sum + s.count, 0)
  const pecasNoPeriodo = stats.reduce((sum, s) => sum + s.quantidade, 0)
  const mesAtual = stats[stats.length - 1]
  const primeiroMes = stats[0]
  const periodoAnalisado =
    totalMeses === 1
      ? primeiroMes.label
      : `${primeiroMes.label} a ${mesAtual.label}`

  return (
    <section className="mb-8" aria-labelledby="dashboard-mensal-title">
      <div className="mb-4">
        <p className="text-sm font-semibold tracking-widest uppercase text-[#0877c5] mb-1">
          Análise
        </p>
        <h3 id="dashboard-mensal-title" className="text-xl font-bold text-gray-900">
          Dashboard mensal
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Resumo das solicitações de orçamento, mês a mês.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-0.5">Período analisado</p>
          <p className="text-lg font-bold text-[#0877c5] capitalize leading-tight">
            {periodoAnalisado}
          </p>
          <p className="text-[11px] text-gray-400 mt-1">
            {totalMeses} {totalMeses === 1 ? 'mês' : 'meses'} com solicitações
          </p>
        </div>
        <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-0.5">Total de solicitações</p>
          <p className="text-xl font-bold text-gray-900">{leadsNoPeriodo}</p>
          <p className="text-[11px] text-gray-400 mt-1">orçamentos no período</p>
        </div>
        <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-0.5">Peças solicitadas</p>
          <p className="text-xl font-bold text-[#b51625]">
            {pecasNoPeriodo.toLocaleString('pt-BR')}
          </p>
          <p className="text-[11px] text-gray-400 mt-1">soma de todas as quantidades</p>
        </div>
        <div className="rounded-xl border border-gray-200/80 bg-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-0.5">Mês mais recente</p>
          <p className="text-lg font-bold text-gray-900 capitalize">{mesAtual.label}</p>
          <p className="text-[11px] text-gray-400 mt-1">
            {mesAtual.count} {mesAtual.count === 1 ? 'solicitação' : 'solicitações'} ·{' '}
            {mesAtual.quantidade.toLocaleString('pt-BR')} peças
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200/80 bg-white p-5 shadow-sm">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Solicitações por mês</h4>
          <ul className="space-y-3" role="list">
            {stats.map((stat) => {
              const pct = Math.round((stat.count / maxCount) * 100)
              return (
                <li key={stat.key}>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span className="font-medium capitalize">{stat.label}</span>
                    <span>{stat.count}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#0877c5] to-[#0877c5]/70 transition-all"
                      style={{ width: `${pct}%` }}
                      role="presentation"
                    />
                  </div>
                </li>
              )
            })}
          </ul>
        </div>

        <div className="rounded-xl border border-gray-200/80 bg-white p-5 shadow-sm">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Peças solicitadas por mês</h4>
          <ul className="space-y-3" role="list">
            {stats.map((stat) => {
              const pct = Math.round((stat.quantidade / maxQuantidade) * 100)
              return (
                <li key={stat.key}>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span className="font-medium capitalize">{stat.label}</span>
                    <span>{stat.quantidade.toLocaleString('pt-BR')}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#b51625] to-[#b51625]/70 transition-all"
                      style={{ width: `${pct}%` }}
                      role="presentation"
                    />
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default LeadsMonthlyDashboard
